import React, { useMemo, useState } from "react";
import ReservationYMap from "src/components/Reservation/components/ReservationYMap/ReservationYMap";
import Dialog from "@mui/material/Dialog";
import { Alert, AlertTitle, Stack } from "@mui/material";
import ProductItem from "src/components/ReservationProduct/components/ProductItem";
import DetailModal from "src/components/ReservationProduct/components/DetailModal";
import ReservationLayout from "src/components/ReservationProduct/components/ReservationLayout";
import LoadMoreBlock from "src/components/common/LoadMoreBlock/LoadMoreBlock";
import { BasketState, ProductCart } from "src/redux/slice/basketSlice";
import { toastOrderingAlert } from "src/components/common/SnackbarAlert/toastAlert";
import {
  EnumMetrikActions,
  sendCustomYandexMetrik,
} from "src/config/analytics/sendCustomYandexMetrik";
import { useActions } from "src/utils/hooks/redux/useActions";
import { useRouter } from "next/router";
import { Box } from "@mui/system";
import {
  FILTER_OPTION,
  FilterModalCheckbox,
} from "src/components/ReservationProduct/components/FiltersModal/FiltersModal";
import { v4 as uuidv4 } from "uuid";
import { useMapOffers } from "src/components/ReservationProduct/hooks/useMapOffers";
import { ProductOffersItemPharmaciesSchema2 } from "src/models/productPharmacies/getProductPharmacies";
import OffersListLoader from "src/components/ReservationProduct/components/OffersListLoader";
import { SearchAddressSchema } from "src/models/findMapAddress/autocompleteSearchAddress";

interface ReservationProductModalProps {
  isOpen: boolean;
  closeModal: () => void;
  productSlug: string;
  imgSource: string;
}

export interface ReservationProductParams {
  offerCompanyId: number;
  offerProductId: number;
}

export enum AddressPlacemarkState {
  show = "show",
  hide = "hide",
}

const priceSortWithPrior = (
  a: ProductOffersItemPharmaciesSchema2,
  b: ProductOffersItemPharmaciesSchema2,
  filterOption: FILTER_OPTION
) => {
  if (a.offer.isListPriorOffer && b.offer.isListPriorOffer) return 0;
  if (a.offer.isListPriorOffer && !b.offer.isListPriorOffer) return -1;
  if (!a.offer.isListPriorOffer && b.offer.isListPriorOffer) return 1;
  if (filterOption === FILTER_OPTION.BuyPrice) {
    if (a.offer.allowCart && b.offer.allowCart) {
      if (a.offer.price === b.offer.price) return 0;
      return a.offer.price > b.offer.price ? 1 : -1;
    }
    if (a.offer.allowCart && !b.offer.allowCart) return -1;
    if (!a.offer.allowCart && b.offer.allowCart) return 1;
  }
  if (a.offer.price === b.offer.price) return 0;

  return a.offer.price > b.offer.price ? 1 : -1;
};

const ReservationProduct: React.FC<ReservationProductModalProps> = ({
  isOpen,
  closeModal,
  productSlug,
  imgSource,
}) => {
  const [selectedPharmacy, setSelectedPharmacy] = useState(0);
  const [coords, setCoords] = useState<[number, number]>();
  const [selectedAddressPharmacy, setSelectedAddressPharmacy] = useState("");
  const [addressPlacemark, setAddressPlacemark] =
    useState<AddressPlacemarkState | null>(null);
  const [isOpenMap, setOpenMap] = useState(true);
  const router = useRouter();
  const [isOpenDetailModal, setOpenDetailModal] = useState(false);
  const [isOpenReservationPanel, setOpenReservationPanel] = useState(false);
  const [filtersData, setFiltersData] = useState<FilterModalCheckbox>({
    underTheOrder: true,
    available: true,
    aroundTheClock: false,
  });
  const [filterOption, setFilterOption] = useState<FILTER_OPTION>(
    FILTER_OPTION.BuyPrice
  );

  const {
    dataMapOffers,
    isLoading,
    currentPageSize,
    handleShowMore,
    placemarkReady,
    handlePlacemarkLoad,
  } = useMapOffers(productSlug, filtersData);

  const pharmacyHandler = (number: number) => {
    setSelectedPharmacy(number);
  };

  const onClickAlert = () => {
    sendCustomYandexMetrik(EnumMetrikActions.goToBasketFromAlert);
    return router.push("/basket");
  };

  const { addProduct } = useActions();

  const reservationProductHandler = (
    { offerCompanyId, offerProductId }: ReservationProductParams,
    basketData: Omit<ProductCart, "slug">
  ) => {
    const actionObj: BasketState = {
      [offerCompanyId]: {
        [offerProductId]: { ...basketData, slug: productSlug },
      },
    };
    addProduct(actionObj);

    toastOrderingAlert(onClickAlert);
    sendCustomYandexMetrik(EnumMetrikActions.showAlertGoodsAddedToBasket);
    closeModal();
  };

  const onClickFiltersSubmit = (data: FilterModalCheckbox) => {
    setFiltersData(data);
  };
  const addressList = useMemo(
    () =>
      dataMapOffers?.reduce<SearchAddressSchema[]>((acc, { items }) => {
        acc.push(
          ...items.map((item) => ({
            display_name: item.pharm.address,
            pharm_name: item.pharm.name,
            longitude: item.pharm.longitude,
            latitude: item.pharm.latitude,
            id: uuidv4(),
          }))
        );
        return acc;
      }, []) || [],
    [dataMapOffers]
  );

  const fullDataListOffers = useMemo(
    () =>
      dataMapOffers?.reduce<ProductOffersItemPharmaciesSchema2[]>(
        (acc, { items }) => [...acc, ...items],
        []
      ) || [],
    [dataMapOffers]
  );

  const sortedOffers = useMemo(() => {
    return fullDataListOffers.sort((a, b) =>
      priceSortWithPrior(a, b, filterOption)
    );
  }, [fullDataListOffers]);

  const filteredOffers = useMemo(() => {
    const sortedOffersByPrice = sortedOffers.sort((a, b) =>
      priceSortWithPrior(a, b, filterOption)
    );

    if (selectedAddressPharmacy !== "") {
      return sortedOffersByPrice.filter(
        (value) => value.pharm.address === selectedAddressPharmacy
      );
    } else {
      return sortedOffersByPrice;
    }
  }, [sortedOffers, selectedAddressPharmacy]);

  const isComputingSort =
    sortedOffers.length === 0 && dataMapOffers?.length !== 0;

  return (
    <>
      <Dialog fullScreen fullWidth open={isOpen} sx={{ height: "100%" }}>
        <ReservationLayout
          setSelectedAddressPharmacy={setSelectedAddressPharmacy}
          addressList={addressList}
          setCoords={setCoords}
          setAddressPlacemark={setAddressPlacemark}
          setFiltersData={setFiltersData}
          onClickFiltersSubmit={onClickFiltersSubmit}
          filterOption={filterOption}
          setFilterOption={setFilterOption}
          closeModal={() => {
            closeModal();
            setOpenReservationPanel(false);
          }}
          setOpenMap={setOpenMap}
          isOpenMap={isOpenMap}
          setOpenReservationPanel={setOpenReservationPanel}
        >
          {!isOpenMap && (
            <ReservationYMap
              handlePlacemarkLoad={handlePlacemarkLoad}
              placemarkReady={placemarkReady}
              dataMapOffers={dataMapOffers}
              productSlug={productSlug}
              filtersData={filtersData}
              setAddressPlacemark={setAddressPlacemark}
              reservationProductHandler={reservationProductHandler}
              isOpenReservationPanel={isOpenReservationPanel}
              isOpenMap={isOpenMap}
              setOpenReservationPanel={setOpenReservationPanel}
              coords={coords}
              addressPlacemark={addressPlacemark}
              setCoords={setCoords}
              selectedPharmacy={selectedPharmacy}
              pharmacyHandler={pharmacyHandler}
              imgSource={imgSource}
              setOpenDetailModal={setOpenDetailModal}
            />
          )}
          {isOpenMap && (
            <Box
              sx={{
                paddingBottom: {
                  xs: "70px",
                  md: "0px",
                },
                height: "100%",
              }}
            >
              <Stack
                spacing={1}
                component="ul"
                sx={{ height: "calc(100% - 72px)", padding: 0, pb: "70px" }}
              >
                {isComputingSort || isLoading ? (
                  <OffersListLoader />
                ) : filteredOffers?.length === 0 ? (
                  <Alert sx={{ width: "100%", mb: 3 }} severity="info">
                    <AlertTitle>
                      По заданным фильтрам ничего не найдено. Пожалуйста,
                      измените фильтры.
                    </AlertTitle>
                  </Alert>
                ) : (
                  filteredOffers
                    ?.slice(0, currentPageSize)
                    .map((value, index: number) => {
                      return (
                        <ProductItem
                          allowCart={value.offer.allowCart}
                          exclusivePrice={value.offer.exclusivePrice}
                          futureBooking={value.offer.futureBooking}
                          isListPriorOffer={value.offer.isListPriorOffer}
                          productName={value.pharmacy_product_name}
                          price={value.offer.price}
                          pharmacyAddress={value.pharm.address}
                          pharmacyName={value.pharm.name}
                          key={value.offer.offerProductId + index}
                          setOpenDetailModal={setOpenDetailModal}
                          imgSrc={imgSource}
                          toBookClick={() => {
                            sendCustomYandexMetrik(
                              EnumMetrikActions.bookingByList
                            );
                            reservationProductHandler(
                              {
                                offerProductId: value.offer.offerProductId,
                                offerCompanyId: value.offer.companyId,
                              },
                              {
                                name: value.pharmacy_product_name,
                                productId: value.offer.offerProductId,
                                address: value.pharm.address,
                                imgSource,
                                pharmacyName: value.offer.companyName,
                                pharmacyPhone: value.pharm.salePhone,
                                pharmacySchedule: value.pharm.saleSchedule,
                                cartInfo: value.pharm.cartInfo,
                                bookingInfo: value.pharm.bookingInfo,
                                minPriceForBuy: value.pharm.minPriceForBuy,
                                fullName: value.pharmacy_product_name || "",
                                mnn: "",
                                price: value.offer.price,
                                brandName: value.pharm.brand_name,
                                counter: 1,
                                quantity: value.offer.quantity,
                              }
                            );
                          }}
                        />
                      );
                    })
                )}
                {filteredOffers.length !== 0 && (
                  <LoadMoreBlock
                    isLoading={isLoading}
                    isHasNextPage={filteredOffers.length >= currentPageSize}
                    onNextPage={handleShowMore}
                  />
                )}
              </Stack>
            </Box>
          )}
        </ReservationLayout>
      </Dialog>
      <DetailModal
        isOpen={isOpenDetailModal}
        onClose={() => setOpenDetailModal(false)}
      />
    </>
  );
};

export default ReservationProduct;
